/**
 * Song Data Client
 *
 * Provides type-safe CRUD operations for songs in the admin workspace.
 * Handles draft/published/archived statuses, search, filtering, and sorting.
 */

import { supabase } from './supabaseClient';

export type SongStatus = 'draft' | 'published' | 'archived';

export interface Song {
  id: string;
  title: string;
  artist: string;
  slug: string;
  description: string | null;
  lyric_spotlight: string | null;
  story_body: string | null;
  image_url: string | null;
  spotify_url: string | null;
  apple_music_url: string | null;
  youtube_url: string | null;
  tags: string[];
  themes: string[];
  region: string | null;
  country: string | null;
  status: SongStatus;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  archived_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  deep_insight: string | null;
  quick_fact: string | null;
  context_in_time: string | null;
  timeline_json: any | null;
  related_song_ids: string[] | null;
  primary_tags?: string[];
}

export interface SongFilters {
  search?: string;
  status?: SongStatus | 'all';
  tags?: string[];
  themes?: string[];
  sortBy?: 'updated_at' | 'title' | 'artist' | 'published_at';
  sortOrder?: 'asc' | 'desc';
  includeDrafts?: boolean;
}

export interface CreateSongData {
  title: string;
  artist: string;
  slug: string;
  description?: string;
  lyric_spotlight?: string;
  story_body?: string;
  image_url?: string;
  spotify_url?: string;
  apple_music_url?: string;
  youtube_url?: string;
  tags?: string[];
  themes?: string[];
  region?: string;
  country?: string;
  status?: SongStatus;
  meta_title?: string;
  meta_description?: string;
}

export interface UpdateSongData extends Partial<CreateSongData> {
  id: string;
}

/**
 * Get all songs with optional filtering, searching, and sorting
 * Note: When includeDrafts is true, ALL songs are returned regardless of status
 */
export async function getSongs(filters: SongFilters = {}): Promise<Song[]> {
  try {
    console.log('[songDataClient] getSongs called with filters:', filters);

    let query = supabase
      .from('songs')
      .select('*');

    // Status filter
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
      console.log('[songDataClient] Filtering by status:', filters.status);
    } else if (!filters.includeDrafts) {
      query = query.eq('status', 'published');
      console.log('[songDataClient] Filtering published only');
    } else {
      console.log('[songDataClient] Including ALL songs (drafts + published)');
    }

    // Search filter (title or artist)
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,artist.ilike.%${filters.search}%`);
    }

    // Tag filter
    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    // Theme filter
    if (filters.themes && filters.themes.length > 0) {
      query = query.contains('themes', filters.themes);
    }

    // Sorting
    const sortBy = filters.sortBy || 'updated_at';
    const sortOrder = filters.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    const { data, error } = await query;

    if (error) {
      console.error('[songDataClient] Error fetching songs:', error);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('[songDataClient] No songs found');
      return [];
    }

    const songIds = data.map(song => song.id);
    const { data: tagMappings } = await supabase
      .from('song_primary_tag_map')
      .select('song_id, primary_tags(name)')
      .in('song_id', songIds);

    const primaryTagsBySongId: Record<string, string[]> = {};
    if (tagMappings) {
      for (const mapping of tagMappings) {
        const songId = mapping.song_id;
        const tagName = (mapping.primary_tags as any)?.name;
        if (tagName) {
          if (!primaryTagsBySongId[songId]) {
            primaryTagsBySongId[songId] = [];
          }
          primaryTagsBySongId[songId].push(tagName);
        }
      }
    }

    const songsWithPrimaryTags = data.map(song => ({
      ...song,
      primary_tags: primaryTagsBySongId[song.id] || []
    }));

    console.log(`[songDataClient] Successfully fetched ${songsWithPrimaryTags.length} songs from Supabase`);
    return (songsWithPrimaryTags as Song[]) || [];
  } catch (err) {
    console.error('[songDataClient] Unexpected error in getSongs:', err);
    return [];
  }
}

/**
 * Get a single song by ID
 */
export async function getSongById(id: string): Promise<Song | null> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('[songDataClient] Error fetching song:', error);
      return null;
    }

    return data as Song | null;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in getSongById:', err);
    return null;
  }
}

/**
 * Get a single song by slug
 */
export async function getSongBySlug(slug: string): Promise<Song | null> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
      console.error('[songDataClient] Error fetching song by slug:', error);
      return null;
    }

    return data as Song | null;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in getSongBySlug:', err);
    return null;
  }
}

/**
 * Create a new song
 */
export async function createSong(songData: CreateSongData): Promise<Song | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('songs')
      .insert({
        ...songData,
        created_by: user?.id || null,
        status: songData.status || 'draft',
        is_active: songData.status === 'published',
        tags: songData.tags || [],
        themes: songData.themes || [],
      })
      .select()
      .single();

    if (error) {
      console.error('[songDataClient] Error creating song:', error);
      return null;
    }

    return data as Song;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in createSong:', err);
    return null;
  }
}

/**
 * Update an existing song
 */
export async function updateSong(songData: UpdateSongData): Promise<Song | null> {
  try {
    const { id, ...updates } = songData;

    const { data, error } = await supabase
      .from('songs')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[songDataClient] Error updating song:', error);
      return null;
    }

    return data as Song;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in updateSong:', err);
    return null;
  }
}

/**
 * Update song status (publish, unpublish)
 */
export async function updateSongStatus(id: string, status: SongStatus): Promise<Song | null> {
  try {
    const updates: any = {
      status,
      is_active: status === 'published',
      updated_at: new Date().toISOString(),
    };

    // Set published_at when publishing, clear it when unpublishing
    if (status === 'published') {
      updates.published_at = new Date().toISOString();
    } else if (status === 'draft') {
      updates.published_at = null;
    }

    const { data, error } = await supabase
      .from('songs')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[songDataClient] Error updating song status:', error);
      return null;
    }

    return data as Song;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in updateSongStatus:', err);
    return null;
  }
}

/**
 * Duplicate a song (creates a draft copy)
 */
export async function duplicateSong(id: string): Promise<Song | null> {
  try {
    const original = await getSongById(id);
    if (!original) {
      console.error('[songDataClient] Original song not found for duplication');
      return null;
    }

    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('songs')
      .insert({
        title: `${original.title} (Copy)`,
        artist: original.artist,
        slug: `${original.slug}-copy-${Date.now()}`,
        description: original.description,
        lyric_spotlight: original.lyric_spotlight,
        story_body: original.story_body,
        image_url: original.image_url,
        spotify_url: original.spotify_url,
        apple_music_url: original.apple_music_url,
        youtube_url: original.youtube_url,
        tags: original.tags,
        themes: original.themes,
        region: original.region,
        country: original.country,
        status: 'draft',
        is_active: false,
        created_by: user?.id || null,
        meta_title: original.meta_title,
        meta_description: original.meta_description,
      })
      .select()
      .single();

    if (error) {
      console.error('[songDataClient] Error duplicating song:', error);
      return null;
    }

    return data as Song;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in duplicateSong:', err);
    return null;
  }
}

/**
 * Archive a song (soft delete) - DEPRECATED
 * Use deleteSong() for permanent removal
 */
export async function archiveSong(id: string): Promise<boolean> {
  console.warn('[songDataClient] archiveSong is deprecated. Use deleteSong() instead.');
  try {
    const { error } = await supabase
      .from('songs')
      .update({
        status: 'archived',
        is_active: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) {
      console.error('[songDataClient] Error archiving song:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in archiveSong:', err);
    return false;
  }
}

/**
 * Permanently delete a song (hard delete - use with caution)
 */
export async function deleteSong(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('songs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('[songDataClient] Error deleting song:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in deleteSong:', err);
    return false;
  }
}

/**
 * Generate a unique slug from title and artist
 */
export function generateSlug(title: string, artist: string): string {
  const combined = `${artist} ${title}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return combined;
}

/**
 * Check if a slug is available
 */
export async function isSlugAvailable(slug: string, excludeId?: string): Promise<boolean> {
  try {
    let query = supabase
      .from('songs')
      .select('id')
      .eq('slug', slug);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('[songDataClient] Error checking slug availability:', error);
      return false;
    }

    return !data;
  } catch (err) {
    console.error('[songDataClient] Unexpected error in isSlugAvailable:', err);
    return false;
  }
}
